import { LIST_TEMPLATE_FONTS, GET_COMPANY_INVOICE_DESIGN } from "queries/neoFact/templates";
import { useLazyQuery, useMutation } from "@apollo/client";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { clientNeoFact } from "@neonumy/api-clients";
import { FormInstance } from "antd";
import { useEffect, useMemo } from "react";
import {
  GET_COMPANY_EMAIL_TEMPLATE,
  CREATE_SALE_DOCUMENT_TEMPLATE,
  DELETE_SALE_DOCUMENT_TEMPLATE,
  UPDATE_COMPANY_EMAIL_TEMPLATE,
  UPDATE_SALE_DOCUMENT_TEMPLATE,
} from "queries/neoFact/settings";

export default function useCostumizationApi(id?: ID, form?: FormInstance) {
  const [
    getCompanyEmailTemplate,
    {
      refetch,
      loading: getCompanyEmailTemplateLoading,
      data: getCompanyEmailTemplateData,
      error: getCompanyEmailTemplateError,
    },
  ] = useLazyQuery<GetCompanyEmailTemplateResponse, GetCompanyEmailTemplateVars>(
    GET_COMPANY_EMAIL_TEMPLATE,
    {
      client: clientNeoFact,
    },
  );

  useEffect(() => {
    if (getCompanyEmailTemplateError) onErrorGraphql(getCompanyEmailTemplateError);
  }, [getCompanyEmailTemplateError]);

  const emailTemplateData = getCompanyEmailTemplateData?.getCompanyEmailTemplate;

  const [updateCompanyEmailTemplate, { loading: updateTemplateLoading }] = useMutation<
    UpdateCompanyEmailTemplateMutationResponse,
    UpdateCompanyEmailTemplateVars
  >(UPDATE_COMPANY_EMAIL_TEMPLATE, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [deleteSaleDocumentTemplate] = useMutation<
    DeleteSaleDocumentTemplateResponse,
    DeleteSaleDocumentTemplateVars
  >(DELETE_SALE_DOCUMENT_TEMPLATE, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [createUpdateSaleDocumentTemplate] = useMutation<
    CreateUpdateSaleDocumentTemplateMutationResponse,
    CreateUpdateSaleDocumentTemplateVars
  >(id ? UPDATE_SALE_DOCUMENT_TEMPLATE : CREATE_SALE_DOCUMENT_TEMPLATE, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [listTemplateFonts, { data: listTemplateFontsData, error: listTemplateFontsError }] =
    useLazyQuery<ListTemplateFontsResponse>(LIST_TEMPLATE_FONTS, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (listTemplateFontsError) onErrorGraphql(listTemplateFontsError);
  }, [listTemplateFontsError]);

  const templateFonts = useMemo(
    () =>
      listTemplateFontsData?.listTemplateFonts?.results?.map((item: TemplateFont) => ({
        key: item.id,
        label: item.name,
        value: item.id,
      })) ?? [],
    [listTemplateFontsData],
  );

  const [getCompanySaleDocumentDesign, { data: getCompanySaleDocumentDesignData }] = useLazyQuery<
    GetCompanySaleDocumentDesignResponse,
    GetCompanySaleDocumentDesignVars
  >(GET_COMPANY_INVOICE_DESIGN, {
    client: clientNeoFact,
  });

  const previewDocumentUrl =
    getCompanySaleDocumentDesignData?.getCompanySaleDocumentDesign?.previewUrl ?? "";

  useEffect(() => {
    const design = getCompanySaleDocumentDesignData?.getCompanySaleDocumentDesign;
    if (!design) return;

    form?.setFields([
      { name: "template", value: design.saleDocumentTemplate?.id },
      { name: "logo", value: design.logoUrl },
      { name: "backgroundImage", value: design.backgroundImageUrl },
      { name: "logoDescription", value: design.logoDescription },
      { name: "font", value: design.fontId },
      { name: "note", value: design.note },
      { name: "latePaymentPenalty", value: design.latePaymentPenalty },
      { name: "fixedRecoveryFee", value: design.fixedRecoveryFee },
      { name: "discount", value: design.discount },
      { name: "titleColor", value: design.titleColor },
      { name: "baseColor", value: design.baseColor },
      { name: "baseTextColor", value: design.baseTextColor },
      { name: "baseTextColorBackground", value: design.baseTextColorBackground },
      { name: "templateLanguage", value: design.templateLanguage },
      { name: "logoPosition", value: design.logoPosition?.value },
      { name: "tableBorder", value: design.tableBorder?.value },
      { name: "backgroundImageOpacity", value: Number(design.backgroundImageOpacity) },
      { name: "productNameOnLine", value: design.productNameOnLine },
    ]);
  }, [getCompanySaleDocumentDesignData, form]);

  return {
    refetch,
    templateFonts,
    listTemplateFonts,
    emailTemplateData,
    previewDocumentUrl,
    updateTemplateLoading,
    getCompanyEmailTemplate,
    deleteSaleDocumentTemplate,
    updateCompanyEmailTemplate,
    getCompanySaleDocumentDesign,
    getCompanyEmailTemplateLoading,
    createUpdateSaleDocumentTemplate,
  };
}
